import type { Theme } from "@mui/material/styles";

/**
 * Table styling shared by the two readers that render article content:
 * DreamMarkdownReader (legacy markdown posts, plus a dozen non-blog surfaces)
 * and BlogRichTextReader (Payload posts). A blog post takes one path or the
 * other depending on how it was authored, so the rules have to match — they
 * were duplicated by hand before this and only stayed in sync by luck.
 */

/**
 * - `scroll` sizes the table to its content and scrolls the overflow. The
 *   default, and what every surface outside the blog still does.
 * - `fit` sizes the table to its container and wraps inside cells instead.
 */
export type ArticleTableLayout = "fit" | "scroll";

export const DEFAULT_ARTICLE_TABLE_LAYOUT: ArticleTableLayout = "scroll";

/**
 * The wrapper around the table. Still scrolls under `fit` — see the cell floor
 * in `tableStyles` — just far less often.
 */
export const tableScrollContainerStyles = (theme: Theme) => `
  width: 100%;
  overflow-x: auto;
  margin-bottom: 1em;
  -webkit-overflow-scrolling: touch;

  &:focus-visible {
    outline: 2px solid ${theme.palette.primary.main};
    outline-offset: 2px;
  }
`;

/**
 * Under `fit`, tables fill the column and wrap inside their cells rather than
 * running off the side. Three rules do that work together:
 *
 * - No `min-width: max-content` on the table. That is what sizes a table to its
 *   longest unwrapped cell and hands the overflow to the scroll wrapper, and it
 *   is all `scroll` needs.
 * - `overflow-wrap: anywhere` on cells, so a long unbroken run (a URL, or
 *   "$124,000 a year, or $59.62 an hour") breaks instead of widening its column
 *   back past the container.
 * - `min-width` on cells as a floor. A two- or three-column table wraps
 *   comfortably at any width, but a six-column table on a ~343px phone column
 *   would otherwise squeeze to a few characters per line; past this floor it
 *   scrolls again, which is the lesser evil.
 *
 * `table-layout` stays at the default `auto` on purpose: it keeps a short label
 * column narrow and gives the surplus to the long detail column, which is the
 * shape of nearly every table our authors write.
 */
export const tableStyles = (theme: Theme, layout: ArticleTableLayout) => `
  table {
    width: 100%;
    ${layout === "fit" ? "max-width: 100%;" : "min-width: max-content;"}
    border-collapse: collapse;
    font-size: 0.95em;
  }

  th,
  td {
    border: 1px solid ${theme.palette.divider};
    padding: 0.65em 0.75em;
    text-align: left;
    vertical-align: top;
    ${layout === "fit" ? "min-width: 7ch;\n    overflow-wrap: anywhere;" : ""}
  }

  th {
    background: ${theme.palette.action.hover};
    font-weight: 700;
  }
`;
